import { useState } from "react"
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from "@mui/material"
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Comment as CommentIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material"
import { useApiQuery, useApiMutation } from "../../hooks/useApi"
import { useQueryClient } from "@tanstack/react-query"
import api from "../../utils/api"
import JobsTable from "../../components/jobs/JobsTable"
import JobDetailsDialog from "../../components/jobs/JobDetailsDialog"

function ProcessingJobsPage() {
  const [selectedJob, setSelectedJob] = useState(null)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [actionDialogOpen, setActionDialogOpen] = useState(false)
  const [dialogType, setDialogType] = useState(null) // "status" | "subStatus" | "comment"
  const [newStatus, setNewStatus] = useState("")
  const [newSubStatus, setNewSubStatus] = useState("")
  const [comments, setComments] = useState("")
  const [fees, setFees] = useState("")
  const [jobDetailsForView, setJobDetailsForView] = useState(null)

  const queryClient = useQueryClient()

  const { data: jobs, isLoading } = useApiQuery(
    ["processing-jobs"],
    `/jobs/processing/`
  )

  const updateJobStatusMutation = useApiMutation(
    `/jobs/${selectedJob?.JobId}/status`,
    "PATCH",
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: "processing-jobs" })
        handleCloseActionDialog()
      },
    }
  )

  const updateJobSubStatusMutation = useApiMutation(
    `/jobs/${selectedJob?.JobId}/sub-status`,
    "PATCH",
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: "processing-jobs" })
        handleCloseActionDialog()
      },
    }
  )

  const setJobStatusAsCompletedMutation = useApiMutation(
    `/jobs/${selectedJob?.JobId}/status/completed`,
    "PATCH",
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: "processing-jobs" })
        handleCloseActionDialog()
      },
    }
  )

  const setJobCommentMutation = useApiMutation(
    `/jobs/${selectedJob?.JobId}/comments`,
    "POST",
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: "processing-jobs" })
        handleCloseActionDialog()
      },
    }
  )

  // Define table columns
  const columns = [
    { id: "jobId", label: "Job ID", field: "JobId", type: "jobId" },
    { id: "client", label: "Client", field: "clientName" },
    { id: "jobType", label: "Job Type", field: "jobType" },
    { id: "dueDate", label: "Due Date", field: "dueDate", type: "date" },
    { id: "status", label: "Status", field: "status", type: "status" },
    {
      id: "subStatus",
      label: "Sub Status",
      field: "subStatus",
      type: "subStatus",
    },
  ]

  // Define actions menu items
  const actions = [
    {
      id: "view",
      label: "View Details",
      icon: <VisibilityIcon />,
    },
    {
      id: "setStatus",
      label: "Set Status",
      icon: <EditIcon />,
    },
    {
      id: "setSubStatus",
      label: "Set Sub Status",
      icon: <EditIcon />,
    },
    {
      id: "addComment",
      label: "Add Comment",
      icon: <CommentIcon />,
    },
  ]

  const handleActionClick = async (action, job) => {
    setSelectedJob(job)

    switch (action.id) {
      case "view":
        // Fetch full job details
        try {
          const jobDetails = await api.get(`/jobs/${job.JobId}`)
          setJobDetailsForView(jobDetails.data[0])
          setViewDialogOpen(true)
        } catch (error) {
          console.error("Failed to fetch job details:", error)
        }
        break
      case "setStatus":
        setDialogType("status")
        setActionDialogOpen(true)
        break
      case "setSubStatus":
        setDialogType("subStatus")
        setActionDialogOpen(true)
        break
      case "addComment":
        setDialogType("comment")
        setActionDialogOpen(true)
        break
      default:
        break
    }
  }

  const handleCloseActionDialog = () => {
    setActionDialogOpen(false)
    setDialogType(null)
    setNewStatus("")
    setNewSubStatus("")
    setComments("")
    setFees("")
    setSelectedJob(null)
  }

  const handleConfirmUpdate = () => {
    let data = {}

    if (dialogType === "status") {
      data = {
        status: newStatus,
        reason: comments || "",
      }
      if (newStatus === "completed") {
        data.fees = fees
        setJobStatusAsCompletedMutation.mutate(data)
      } else {
        updateJobStatusMutation.mutate(data)
      }
    } else if (dialogType === "subStatus") {
      data = {
        subStatus: newSubStatus,
        reason: comments || "",
      }
      updateJobSubStatusMutation.mutate(data)
    } else if (dialogType === "comment") {
      data = {
        commentText: comments || "",
      }
      setJobCommentMutation.mutate(data)
    }
  }

  if (isLoading) {
    return <div>Loading....</div>
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        All Processing Jobs
      </Typography>
      <Typography variant="body1" color="textSecondary" paragraph>
        These are all accepted jobs.
      </Typography>

      {/* Jobs Table with Reusable Component */}
      <JobsTable
        jobs={jobs || []}
        columns={columns}
        actions={actions}
        onActionClick={handleActionClick}
        emptyMessage="No jobs in this status"
      />

      {/* View Details Dialog - Reusable Component */}
      <JobDetailsDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false)
          setJobDetailsForView(null)
        }}
        job={jobDetailsForView}
      />

      {/* Action Dialog (Status, SubStatus, Comment) */}
      <Dialog
        open={actionDialogOpen}
        onClose={handleCloseActionDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dialogType === "status"
            ? "Update Job Status"
            : dialogType === "subStatus"
            ? "Update Job Sub Status"
            : "Add Comment"}
        </DialogTitle>
        <DialogContent>
          {selectedJob && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="h6" gutterBottom>
                {selectedJob.JobId} - {selectedJob.jobType}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Client: {selectedJob.clientName}
              </Typography>

              {dialogType === "status" && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>New Status</InputLabel>
                  <Select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    label="New Status"
                  >
                    <MenuItem value="cancelled">Canceled By Client</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </Select>
                </FormControl>
              )}

              {newStatus === "completed" && (
                <TextField
                  fullWidth
                  label="Fees"
                  type="number"
                  value={fees}
                  onChange={(e) => setFees(e.target.value)}
                  sx={{ mb: 2 }}
                />
              )}

              {dialogType === "subStatus" && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>New Sub Status</InputLabel>
                  <Select
                    value={newSubStatus}
                    onChange={(e) => setNewSubStatus(e.target.value)}
                    label="New Sub Status"
                  >
                    <MenuItem value="documentsPending">
                      Documents Pending
                    </MenuItem>
                    <MenuItem value="taxPending">Tax Pending</MenuItem>
                    <MenuItem value="verificationPending">
                      Verification Pending
                    </MenuItem>
                    <MenuItem value="clientNotResponding">
                      Client Not Responding
                    </MenuItem>
                    <MenuItem value="keptOnHold">Kept on Hold</MenuItem>
                    <MenuItem value="none">None</MenuItem>
                  </Select>
                </FormControl>
              )}

              {(dialogType === "status" ||
                dialogType === "subStatus" ||
                dialogType === "comment") && (
                <TextField
                  fullWidth
                  label="Comments"
                  multiline
                  rows={3}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="Add notes about the update..."
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseActionDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmUpdate}
            variant="contained"
            disabled={
              (dialogType === "status" &&
                (!newStatus || (newStatus === "completed" && !fees))) ||
              (dialogType === "subStatus" && !newSubStatus) ||
              (dialogType === "comment" && !comments)
            }
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ProcessingJobsPage